import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Col,
  Row,
  Typography,
  Layout,
  Button,
  Card,
  Image,
  Divider,
  Rate,
  Modal,
} from "antd";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { ImageUrl } from "../../helpers/Functions";
import Slider from "react-slick";
import "../contact-us/contact-us.css";
import "./homepage.css";

const HOMEPAGE_COUNTER_TARGETS = [8200, 12200, 50000, 70000];
const HOMEPAGE_COUNTER_DURATION_MS = 2200;

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

function Homepage() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.userData);
  const token = useSelector((state) => state.user.userToken);
  const videoRef = useRef(null);
  const [modal2Open, setModal2Open] = useState(false);
  const counterSectionRef = useRef(null);
  const counterAnimatedRef = useRef(false);
  const counterRafRef = useRef(null);
  const [counterValues, setCounterValues] = useState(() =>
    HOMEPAGE_COUNTER_TARGETS.map(() => 0)
  );

  useEffect(() => {
    if (modal2Open && videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.log("Autoplay failed:", error);
      });
    } else if (!modal2Open && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [modal2Open]);

  React.useEffect(() => {
    if (user && token) {
      navigate("/", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const testimonials = [
    {
      title: "Efficient",
      quote:
        "The course quality and affordability make this the best learning platform I’ve come across. I love that I can access my mentor 24/7 for any questions I have—absolutely worth it!",
      name: "David Kowalski",
      rating: 4,
      image: "ellipse1.png",
    },
    {
      title: "Motivating",
      quote:
        "This platform has completely transformed my learning experience! The one-on-one mentorship is invaluable, and I’ve seen tremendous growth in a short time. Highly recommended!",
      name: "Linda Torres",
      rating: 5,
      image: "ellipse1.png",
    },
    {
      title: "Progressive",
      quote:
        "I was blown away by how easy it was to collaborate with others using the whiteboard feature. The courses are top-notch, and I feel fully supported every step of the way.",
      name: "Raymond James",
      rating: 4,
      image: "ellipse1.png",
    },
    {
      title: "Credible",
      quote:
        "Enrolling in the popular courses was the best decision I made for my career! The content is engaging, and the instructors are incredibly knowledgeable. I can’t wait to take more courses!",
      name: "Angela Patel",
      rating: 4,
      image: "ellipse1.png",
    },
    {
      title: "Impactful",
      quote:
        "The flexibility of learning at my own pace, combined with expert guidance, has been a game-changer. The affordable prices make it easy to keep advancing my skills without financial stress.",
      name: "Cody baker",
      rating: 5,
      image: "ellipse1.png",
    },
    {
      title: "Structured",
      quote:
        "I appreciate the personalized attention I receive from my mentor. Their insights have helped me grasp complex concepts quickly. I feel confident in my learning journey!",
      name: "zack francies",
      rating: 4,
      image: "ellipse1.png",
    },
  ];

  const settings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    initialSlide: 0,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          infinite: true,
          dots: false,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          initialSlide: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
    ],
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const section = counterSectionRef.current;
    if (!section) return undefined;

    const finishToTargets = () => {
      setCounterValues([...HOMEPAGE_COUNTER_TARGETS]);
    };

    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      finishToTargets();
      counterAnimatedRef.current = true;
      return undefined;
    }

    const runAnimation = () => {
      const start = performance.now();

      const tick = (now) => {
        const elapsed = now - start;
        const t = Math.min(1, elapsed / HOMEPAGE_COUNTER_DURATION_MS);
        const eased = easeOutCubic(t);
        setCounterValues(
          HOMEPAGE_COUNTER_TARGETS.map((target) => Math.floor(target * eased))
        );
        if (t < 1) {
          counterRafRef.current = requestAnimationFrame(tick);
        } else {
          finishToTargets();
          counterRafRef.current = null;
        }
      };

      counterRafRef.current = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || counterAnimatedRef.current) return;
        counterAnimatedRef.current = true;
        runAnimation();
      },
      { threshold: 0.2, rootMargin: "0px 0px -8% 0px" }
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
      if (counterRafRef.current != null) {
        cancelAnimationFrame(counterRafRef.current);
        counterRafRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Childhood Education Consulting & Management - WrightCo Academy</title>
        <meta
          name="description"
          content="Get expert early education consulting, tailored management solutions, and environmental strategies. Partner with WrightCo Academy to help your center thrive!"
        />
        <meta
          property="og:title"
          content="Childhood Education Consulting & Management - WrightCo Academy"
        />
        <meta
          property="og:description"
          content="Get expert early education consulting, tailored management solutions, and environmental strategies. Partner with WrightCo Academy to help your center thrive!"
        />
      </Helmet>
      <Layout className="tutor-home" style={{ minHeight: "100vh", backgroundSize: "cover" }}>
        <section style={{ padding: "80px 0", background: "#f9fafb" }}>
          <Row
            gutter={[40, 40]}
            align="middle"
            justify="space-between"
            style={{
              maxWidth: "1200px",
              margin: "0 auto",
              padding: "0 20px",
            }}
          >
            <Col xs={24} md={12}>
              <div>
                <Typography.Title
                  level={1}
                  style={{
                    fontWeight: 700,
                    color: "#111827",
                    lineHeight: "1.2",
                    marginBottom: "20px",
                  }}
                >
                  Welcome to <span style={{ color: "#00a79d" }}>WrightCo Academy</span>
                </Typography.Title>

                <Typography.Paragraph
                  style={{
                    fontSize: "16px",
                    color: "#4b5563",
                    lineHeight: "1.7",
                    marginBottom: "30px",
                  }}
                >
                  WrightCo Academy is committed to delivering high-quality, specialized
                  courses tailored to the Early Care and Education industry. Our
                  platform supports the full spectrum of organizational training—from
                  leadership to operational services—through flexible learning formats
                  including in-person, virtual, on-site, and on-demand programs.
                </Typography.Paragraph>

                <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
                  <Button
                    size="large"
                    className="loginButton"
                    style={{
                      height: "48px",
                      padding: "0 28px",
                      borderRadius: "8px",
                      fontWeight: 600,
                      boxShadow: "0 8px 20px rgba(22,119,255,0.2)",
                    }}
                    onClick={() => window.open("https://wrightcoacademy.com/signin", "_blank")}
                  >
                    Join as Trainer
                  </Button>

                  <Button
                    size="large"
                    style={{
                      height: "48px",
                      padding: "0 28px",
                      borderRadius: "8px",
                      fontWeight: 600,
                      border: "1px solid #d1d5db",
                      background: "#fff",
                    }}
                    onClick={() => setModal2Open(true)}
                  >
                    ▶ Watch Demo
                  </Button>
                </div>
              </div>
            </Col>

            <Col xs={24} md={12}>
              <div
                style={{
                  textAlign: "center",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "-20px",
                    right: "-20px",
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(135deg, #1677ff20, #00c6ff20)",
                    borderRadius: "20px",
                    zIndex: 0,
                  }}
                />

                <Image
                  preview={false}
                  alt="Banner"
                  src={ImageUrl("homebannerbg.png")}
                  style={{
                    maxWidth: "100%",
                    borderRadius: "20px",
                    position: "relative",
                    zIndex: 1,
                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  }}
                />
              </div>
            </Col>
          </Row>
        </section>

        <section
          ref={counterSectionRef}
          className="counter-section"
          style={{ padding: "70px 0" }}
        >
          <Row justify="center">
            <Col xs={22} lg={20}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "30px",
                  textAlign: "center",
                }}
              >
                <div className="counter-card">
                  <h2 className="counter-number">
                    {counterValues[0].toLocaleString("en-US")}+
                  </h2>
                  <p className="counter-text">Success Stories</p>
                </div>
                <div className="counter-card">
                  <h2 className="counter-number">
                    {counterValues[1].toLocaleString("en-US")}+
                  </h2>
                  <p className="counter-text">Expert Mentors</p>
                </div>
                <div className="counter-card">
                  <h2 className="counter-number">
                    {counterValues[2].toLocaleString("en-US")}+
                  </h2>
                  <p className="counter-text">Hours of Courses</p>
                </div>
                <div className="counter-card">
                  <h2 className="counter-number">
                    {counterValues[3].toLocaleString("en-US")}+
                  </h2>
                  <p className="counter-text">Active Students</p>
                </div>
              </div>
            </Col>
          </Row>
        </section>

        <section style={{ padding: "80px 0", background: "#f8fafc" }}>
          <Row justify="center">
            <Col xs={22} md={20}>
              <div style={{ textAlign: "center", marginBottom: "50px" }}>
                <Typography.Title
                  level={2}
                  style={{
                    fontWeight: 700,
                    color: "#0f172a",
                    marginBottom: "10px",
                  }}
                >
                  Benefits of Online Courses
                </Typography.Title>
                <Typography.Text
                  style={{
                    fontSize: "16px",
                    color: "#64748b",
                  }}
                >
                  The platform offers flexibility, accessibility, cost-effective and
                  self-paced learning for everyone.
                </Typography.Text>
              </div>

              <Row gutter={[25, 25]}>
                <Col xs={24} md={8}>
                  <Card className="benefit-card" bordered={false}>
                    <Image preview={false} width={70} src={ImageUrl("ben-1.png")} />
                    <h3>One-on-One Monitoring</h3>
                    <p>
                      Get personalized guidance and track your progress with dedicated
                      support.
                    </p>
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card className="benefit-card" bordered={false}>
                    <Image preview={false} width={70} src={ImageUrl("ben-2.png")} />
                    <h3>24/7 Mentor Support</h3>
                    <p>
                      Access expert help anytime and never miss a learning opportunity.
                    </p>
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card className="benefit-card" bordered={false}>
                    <Image preview={false} width={70} src={ImageUrl("ben-4.png")} />
                    <h3>Affordable Pricing</h3>
                    <p>
                      Learn without breaking the bank with budget-friendly plans for
                      everyone.
                    </p>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </section>

        <Row justify="center">
          <Modal
            open={modal2Open}
            onCancel={() => setModal2Open(false)}
            footer={null}
            centered
            width={900}
            closable
            styles={{
              body: {
                padding: 0,
                borderRadius: "16px",
                overflow: "hidden",
                background: "#000",
              },
            }}
          >
            <div style={{ position: "relative" }}>
              <video
                ref={videoRef}
                controls
                style={{
                  width: "100%",
                  height: "100%",
                  maxHeight: "500px",
                  objectFit: "cover",
                }}
              >
                <source src="/images/tutor.mp4" type="video/mp4" />
              </video>
              <div
                style={{
                  position: "absolute",
                  top: "15px",
                  left: "15px",
                  background: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  padding: "6px 12px",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
              >
                Demo Video
              </div>
            </div>
          </Modal>
        </Row>

        <section
          style={{
            padding: "100px 0",
            background: "radial-gradient(circle at top, #eef6ff, #ffffff)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Row justify="center">
            <Col xs={22} md={20}>
              <Row gutter={[60, 60]} align="middle">
                <Col xs={24} md={12}>
                  <div style={{ position: "relative" }}>
                    <div
                      style={{
                        position: "absolute",
                        top: "20px",
                        left: "20px",
                        width: "100%",
                        height: "100%",
                        background: "linear-gradient(135deg, #1677ff30, #22c55e25)",
                        borderRadius: "25px",
                        zIndex: 0,
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: "-20px",
                        right: "-20px",
                        width: "80px",
                        height: "80px",
                        background: "#1677ff",
                        borderRadius: "20px",
                        opacity: 0.15,
                        zIndex: 1,
                      }}
                    />
                    <Image
                      preview={false}
                      src={ImageUrl("learnersonline.png")}
                      style={{
                        width: "100%",
                        borderRadius: "22px",
                        position: "relative",
                        zIndex: 2,
                        boxShadow: "0 25px 60px rgba(0,0,0,0.15)",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: "20px",
                        left: "20px",
                        background: "#fff",
                        padding: "12px 16px",
                        borderRadius: "14px",
                        boxShadow: "0 15px 30px rgba(0,0,0,0.1)",
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#1677ff",
                        zIndex: 3,
                      }}
                    >
                      📚 Lifetime Learning
                    </div>
                  </div>
                </Col>

                <Col xs={24} md={12}>
                  <div>
                    <span
                      style={{
                        background: "#e6f4ff",
                        color: "#00a79d",
                        padding: "6px 12px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      OUR PHILOSOPHY
                    </span>

                    <Typography.Title
                      level={2}
                      style={{
                        marginTop: "15px",
                        fontWeight: 800,
                        color: "#0f172a",
                        lineHeight: "1.3",
                      }}
                    >
                      Learning is a treasure that accompanies its owner for a life time.
                    </Typography.Title>

                    <p
                      style={{
                        fontSize: "16px",
                        color: "#475569",
                        lineHeight: "1.9",
                        marginTop: "15px",
                      }}
                    >
                      At WrightCo Academy, we believe that knowledge is the most enduring
                      investment an organization can make. That belief drives everything we
                      do - from the courses we design to the communities we serve. We
                      deliver specialized, high-quality training tailored to the Early Care
                      and Education industry, equipping every member of your organization
                      with skills that last far beyond the classroom.
                    </p>

                    <div
                      style={{
                        marginTop: "25px",
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                      }}
                    >
                      <Button
                        size="large"
                        className="loginButton"
                        style={{
                          height: "50px",
                          padding: "0 30px",
                          borderRadius: "12px",
                          fontWeight: 600,
                          boxShadow: "0 10px 25px rgba(22,119,255,0.25)",
                        }}
                        onClick={() => window.open("/about-us", "_blank")}
                      >
                        About Us
                      </Button>
                      <span style={{ color: "#94a3b8", fontSize: "14px" }}>
                        Learn how we transform education 🚀
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </section>

        <section className="students-say">
          <Row style={{ justifyContent: "center" }}>
            <Col xs={24} md={19}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  padding: "10px",
                }}
              >
                <Typography.Title className="fontFamily1 text-35">
                  What our Students say
                </Typography.Title>
                <Typography.Text
                  className="fontFamily1"
                  style={{
                    fontSize: "16px",
                    color: "grey",
                    textAlign: "center",
                  }}
                >
                  Discover firsthand experiences from our students who have transformed
                  their learning journey with us.
                </Typography.Text>
              </div>
              <Row
                style={{ justifyContent: "center", alignItems: "center" }}
                gutter={[32, 32]}
                className="gutter-0"
              >
                <Col lg={24}>
                  <Slider {...settings}>
                    {testimonials.map((testimonial, index) => (
                      <div key={index}>
                        <div className="testicard">
                          <h5>{testimonial.title}</h5>
                          <Image
                            preview={false}
                            alt="Failed to load image"
                            width={30}
                            src={ImageUrl("coma-new.png")}
                            style={{ margin: "8px 0" }}
                          />
                          <p>{testimonial.quote}</p>
                          <Divider />
                          <div className="media-object">
                            <Image
                              preview={false}
                              alt="Failed to load image"
                              src={ImageUrl(testimonial.image)}
                            />
                            <div>
                              <h6>{testimonial.name}</h6>
                              <Rate disabled defaultValue={testimonial.rating} />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </Slider>
                </Col>
              </Row>
            </Col>
          </Row>
        </section>

        <section className="students-say">
          <Row style={{ backgroundColor: "", justifyContent: "center" }}>
            <Col xs={24} md={18}>
              <div className="contact-wraper">
                <Row
                  style={{ justifyContent: "center", alignItems: "center" }}
                  gutter={[32, 32]}
                  className="gutter-0"
                >
                  <Col lg={24}>
                    <div className="contact-heading-box">
                      <h2>Get in touch</h2>
                      <p>
                        Reach out, and let's create a universe of possibilities
                        together!
                      </p>
                    </div>
                  </Col>
                  <Col lg={20}>
                    <div className="home-contact-inner-box">
                      <Row gutter={[16, 16]} align={"middle"}>
                        <Col lg={12}>
                          <h5>Let’s connect constellations</h5>
                          <p>
                            Let's align our constellations! Reach out and let the
                            magic of collaboration illuminate our skies.
                          </p>
                          <form
                            name="insightly_web_to_lead"
                            action="https://crm.na1.insightly.com/WebToLead/Create"
                            method="post"
                            className="contactForm insightly-lead-form home-insightly-lead-form"
                            acceptCharset="UTF-8"
                            autoComplete="on"
                          >
                            <input type="hidden" name="formId" value="lwa1NcKfUM0Y1FzKDL3QZw==" />
                            <input type="hidden" name="dekv" value="2" />
                            <input
                              type="hidden"
                              id="home_insightly_ResponsibleUser"
                              name="ResponsibleUser"
                              value="533219"
                            />
                            <input
                              type="hidden"
                              id="home_insightly_LeadSource"
                              name="LeadSource"
                              value="194411"
                            />

                            <Row gutter={[16, 16]}>
                              <Col lg={12} md={12} sm={24} xs={24}>
                                <div className="insightly-field">
                                  <label className="insightly-field-label" htmlFor="home_insightly_firstName">
                                    First name <span className="insightly-required">*</span>
                                  </label>
                                  <input
                                    id="home_insightly_firstName"
                                    name="FirstName"
                                    type="text"
                                    className="insightly-input web-input"
                                    placeholder="Enter first name"
                                    required
                                  />
                                </div>
                              </Col>
                              <Col lg={12} md={12} sm={24} xs={24}>
                                <div className="insightly-field">
                                  <label className="insightly-field-label" htmlFor="home_insightly_lastName">
                                    Last name <span className="insightly-required">*</span>
                                  </label>
                                  <input
                                    id="home_insightly_lastName"
                                    name="LastName"
                                    type="text"
                                    className="insightly-input web-input"
                                    placeholder="Enter last name"
                                    required
                                  />
                                </div>
                              </Col>
                              <Col lg={24} md={24} sm={24} xs={24}>
                                <div className="insightly-field">
                                  <label className="insightly-field-label" htmlFor="home_insightly_organization">
                                    Organization
                                  </label>
                                  <input
                                    id="home_insightly_organization"
                                    name="OrganizationName"
                                    type="text"
                                    className="insightly-input web-input"
                                    placeholder="Organization (optional)"
                                  />
                                </div>
                              </Col>
                              <Col lg={24} md={24} sm={24} xs={24}>
                                <div className="insightly-field">
                                  <label className="insightly-field-label" htmlFor="home_insightly_Email">
                                    Email <span className="insightly-required">*</span>
                                  </label>
                                  <input
                                    id="home_insightly_Email"
                                    name="email"
                                    type="text"
                                    className="insightly-input web-input"
                                    placeholder="Enter email"
                                    required
                                  />
                                </div>
                              </Col>
                              <Col lg={24} md={24} sm={24} xs={24}>
                                <div className="insightly-field">
                                  <label className="insightly-field-label" htmlFor="home_insightly_Phone">
                                    Phone
                                  </label>
                                  <input
                                    id="home_insightly_Phone"
                                    name="phone"
                                    type="text"
                                    className="insightly-input web-input"
                                    placeholder="Phone (optional)"
                                  />
                                </div>
                              </Col>
                              <Col lg={24} md={24} sm={24} xs={24}>
                                <div className="insightly-field">
                                  <label className="insightly-field-label" htmlFor="home_insightly_EmployeeCount">
                                    Number of employees
                                  </label>
                                  <input
                                    id="home_insightly_EmployeeCount"
                                    name="EmployeeCount"
                                    type="text"
                                    className="insightly-input web-input"
                                    placeholder="e.g. 25"
                                  />
                                </div>
                              </Col>
                              <Col lg={24} md={24} sm={24} xs={24}>
                                <div className="insightly-field">
                                  <label className="insightly-field-label" htmlFor="home_insightly_Description">
                                    Message <span className="insightly-required">*</span>
                                  </label>
                                  <textarea
                                    id="home_insightly_Description"
                                    name="Description"
                                    className="insightly-textarea web-input"
                                    rows={4}
                                    placeholder="Subject and how we can help"
                                    required
                                  />
                                </div>
                              </Col>
                              <Col lg={24} md={24} sm={24} xs={24}>
                                <div className="insightly-field insightly-field--submit">
                                  <Button
                                    type="primary"
                                    htmlType="submit"
                                    className="loginButton"
                                    style={{ width: "100%" }}
                                  >
                                    Send
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          </form>
                        </Col>
                        <Col lg={12} style={{ textAlign: "end" }}>
                          <Image
                            preview={false}
                            alt="Failed to load image"
                            src={ImageUrl("contactImg.png")}
                          />
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </section>
      </Layout>
    </>
  );
}

export default Homepage;
